import {
  Activity,
  GitBranch,
  Languages,
  MousePointerClick,
  Timer,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { FrictionPoint, KpiKey } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { displayFieldLabel } from '../../shared/ui/field-label.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { FrictionScore } from '../../shared/ui/FrictionScore.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { StatCard } from '../../shared/ui/StatCard.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import {
  useComparisons,
  useDashboard,
  useFieldMetrics,
  useFunnel,
  useJourneyGraph,
  useRecommendations,
} from '../../shared/api/queries.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';
import { ExportMenu } from '../../shared/ui/ExportMenu.js';
import { AnalysisFreshness } from './AnalysisFreshness.js';
import { RefreshAnalysis } from './RefreshAnalysis.js';
import { ComparisonSummaryCard } from './ComparisonSummaryCard.js';
import { DashboardSummary } from './DashboardSummary.js';
import { FieldMetricsTable } from './FieldMetricsTable.js';
import { FilterBar } from './FilterBar.js';
import { FunnelChart } from './FunnelChart.js';
import { JourneyMixChart } from './JourneyMixChart.js';
import { LanguageBreakdown } from './LanguageBreakdown.js';
import { SessionsChart } from './SessionsChart.js';
import { useAnalyticsFilters } from '../../shared/api/useAnalyticsFilters.js';
import './dashboard.css';

/**
 * Wording of the headline indicators; the API only names their key. The
 * definition restates the measurement rule (functional §4) in the reader's words.
 */
const KPI_LABELS: Record<KpiKey, { label: string; definition: string; icon: LucideIcon }> = {
  sessions: {
    label: 'Usagers mesurés',
    definition: 'Visites ayant commencé une démarche sur la période.',
    icon: Users,
  },
  completion_rate: {
    label: "Taux d'achèvement",
    definition: 'Part des usagers arrivés au bout de leur démarche.',
    icon: Activity,
  },
  hesitations: {
    label: 'Hésitations',
    definition: 'Pauses de plus de 8 secondes sur un champ avant de le remplir.',
    icon: Timer,
  },
  rage_clicks: {
    label: 'Clics répétés',
    definition: 'Au moins 3 clics au même endroit en moins d’une seconde : l’usager insiste.',
    icon: MousePointerClick,
  },
  median_duration_ms: {
    label: 'Temps médian par étape',
    definition: 'Durée typique passée sur une page de la démarche.',
    icon: Timer,
  },
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { project } = useActiveProject();
  const analyticsFilters = useAnalyticsFilters();
  const { filters, queryString } = analyticsFilters;

  const { data, isLoading } = useDashboard(project?.id, queryString);
  const { data: fieldMetrics } = useFieldMetrics(project?.id, queryString);

  // The funnel follows the selected journey, or the busiest one by default.
  const funnelJourneyId = filters.journeyId ?? data?.journeys[0]?.id;
  const { data: funnel, isLoading: funnelLoading } = useFunnel(project?.id, funnelJourneyId, queryString);
  const { data: graph } = useJourneyGraph(filters.journeyId);
  const { data: comparisons } = useComparisons(project?.id);
  const { data: recommendations } = useRecommendations(project?.id, { state: 'pending', pageSize: 1 });

  const fieldKeys = [...new Set((fieldMetrics?.items ?? []).map((item) => item.fieldKey))].sort();
  const frictionPoints = data?.frictionPoints ?? [];
  // Demand and priority only mean something once visitors have left feedback on a step.
  const hasDemand = frictionPoints.some((row) => row.demandScore > 0);

  const frictionColumns: Array<Column<FrictionPoint>> = [
    {
      key: 'step',
      header: 'Point de friction',
      render: (row) => (
        <div className="cell-primary">
          <div>
            <div className="cell-primary__label">
              {row.stepName}
              {row.fieldKey && (
                <span className="cell-primary__field" data-field-key={row.fieldKey}>
                  {' '}
                  · {displayFieldLabel(row.fieldLabel, row.fieldKey)}
                </span>
              )}
            </div>
            <div className="cell-primary__sub">{row.journeyName}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'score',
      header: 'Friction /100',
      render: (row) => <FrictionScore score={row.frictionScore} />,
    },
    {
      key: 'sessions',
      header: 'Usagers concernés',
      align: 'end',
      render: (row) => row.affectedSessions.toLocaleString('fr-FR'),
    },
    {
      key: 'impact',
      header: 'Impact',
      align: 'end',
      render: (row) => Math.round(row.impact).toLocaleString('fr-FR'),
    },
    ...(hasDemand
      ? ([
          {
            key: 'demand',
            header: 'Demande',
            align: 'end',
            render: (row) =>
              row.demandScore === 0 ? <span className="cell-muted">—</span> : row.demandScore,
          },
          {
            key: 'priority',
            header: 'Priorité',
            align: 'end',
            render: (row) =>
              row.priorityScore === 0 ? (
                <span className="cell-muted">—</span>
              ) : (
                <span className="cell-priority" data-testid="priority-score">
                  {row.priorityScore}
                </span>
              ),
          },
        ] satisfies Array<Column<FrictionPoint>>)
      : []),
    {
      key: 'guidance',
      header: 'Guidance',
      render: (row) =>
        row.hasGuidance ? <Badge tone="positive">En ligne</Badge> : <Badge tone="warning">À traiter</Badge>,
    },
  ];

  const periodLabel = data
    ? `${new Date(data.period.from).toLocaleDateString('fr-FR')} → ${new Date(data.period.to).toLocaleDateString('fr-FR')}`
    : undefined;

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        description={project ? `Projet ${project.name}${periodLabel ? ` · ${periodLabel}` : ''}` : undefined}
        actions={
          <>
            <AnalysisFreshness lastAnalysisAt={data?.lastAnalysisAt} />
            <RefreshAnalysis projectId={project?.id} />
            <ExportMenu projectId={project?.id} queryString={queryString} />
          </>
        }
      />

      <FilterBar
        filters={analyticsFilters}
        journeys={data?.journeys ?? []}
        steps={graph?.steps ?? []}
        fieldKeys={fieldKeys}
        dimensions={data?.dimensions}
      />

      <DashboardSummary
        dashboard={data}
        latestComparison={comparisons?.items[0]}
        recommendations={recommendations}
      />

      <div className="dash__kpis">
        {(data?.kpis ?? []).map((kpi) => {
          const meta = KPI_LABELS[kpi.key];
          const Icon = meta.icon;
          return (
            <StatCard
              key={kpi.key}
              icon={<Icon />}
              label={meta.label}
              value={formatKpi(kpi.value, kpi.unit)}
              definition={meta.definition}
              deltaRatio={kpi.deltaRatio}
              lowConfidence={kpi.lowConfidence}
              hint={
                kpi.key === 'median_duration_ms' ? 'Médiane lue sur des paliers de durée fixes' : undefined
              }
            />
          );
        })}
        {isLoading && !data && <p className="dash__loading">Chargement des indicateurs…</p>}
      </div>

      <div className="dash__charts">
        <Card
          title="Sessions et achèvements"
          icon={<Activity size={15} />}
          actions={<Badge tone="brand">Quotidien</Badge>}
        >
          <SessionsChart data={data?.sessionsByDay ?? []} />
        </Card>

        <Card title="Répartition par parcours" icon={<Users size={15} />}>
          <JourneyMixChart journeys={data?.journeys ?? []} />
        </Card>
      </div>

      <Card
        title={funnel ? `Entonnoir · ${funnel.journeyName}` : 'Entonnoir par parcours'}
        icon={<GitBranch size={15} />}
        actions={
          funnelJourneyId && (
            <Button variant="ghost" onClick={() => navigate(`/journeys/${funnelJourneyId}`)}>
              Ouvrir le parcours
            </Button>
          )
        }
        className="dash__section"
      >
        <FunnelChart
          funnel={funnel}
          loading={funnelLoading}
          onSelectStep={(stepId) => analyticsFilters.set({ journeyId: funnelJourneyId, stepId })}
        />
      </Card>

      <ComparisonSummaryCard projectId={project?.id} />

      <Card
        title="Restitution par langue d'affichage"
        icon={<Languages size={15} />}
        className="dash__section"
      >
        <LanguageBreakdown languages={data?.languages ?? []} />
      </Card>

      <Card
        title="Points de friction classés par impact"
        icon={<MousePointerClick size={15} />}
        actions={
          <Button variant="ghost" onClick={() => navigate('/journeys')}>
            Voir les parcours
          </Button>
        }
        padded={false}
        className="dash__section"
      >
        <Table
          columns={frictionColumns}
          rows={frictionPoints}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/journeys/${row.journeyId}`)}
          empty="Aucun point de friction mesuré sur la période."
        />
        {data && (
          <p className="dash__note">
            Un point de friction est un champ où les usagers hésitent, se trompent ou abandonnent. Impact =
            usagers concernés × friction.
            {hasDemand &&
              ' Demande = avis laissés par les usagers sur l’étape. Priorité = friction × demande ÷ 100.'}{' '}
            Les champs mesurés sur moins de {data.minimumSessionCount} usagers sont signalés et n'entrent pas
            dans le classement.
          </p>
        )}
      </Card>

      <Card title="Indicateurs par champ" icon={<Timer size={15} />} padded={false} className="dash__section">
        <FieldMetricsTable
          items={fieldMetrics?.items ?? []}
          totalCount={fieldMetrics?.totalCount ?? 0}
          onRowClick={(row) => navigate(`/journeys/${row.journeyId}`)}
        />
      </Card>
    </>
  );
}

function formatKpi(value: number, unit: string): string {
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'ms') return value === 0 ? '—' : `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)} s`;
  return value.toLocaleString('fr-FR');
}
